// ============================================================================
// AdminLogin — Login con email+password via Supabase Auth
// ============================================================================

import { useState } from "react";
import { ArrowLeft, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { supabase, isSupabaseConfigured } from "../../lib/supabase";

// URL di produzione a cui Supabase rimanda dopo il click del link di reset
// password. Deve essere un URL fisso (non window.location.origin) perché
// durante i test in locale altrimenti il link puntava a http://localhost:3000
// anziché al sito pubblico.
const PROD_URL = "https://web-app-edilizia-fede.pages.dev";

export default function AdminLogin({ onBack, onLogin, recoveryMode = false, onRecoveryComplete }) {
  const [email, setEmail] = useState(() => localStorage.getItem("adminLastEmail") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [recoveryDone, setRecoveryDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Inserisci email e password");
      return;
    }
    if (!isSupabaseConfigured) {
      setError("Supabase non configurato");
      return;
    }
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (err) {
      setError(
        err.message === "Invalid login credentials"
          ? "Email o password non corretti"
          : err.message
      );
      return;
    }
    if (data?.user) {
      localStorage.setItem("adminLastEmail", email.trim());
      onLogin({ id: 0, name: "Amministratore", isAdmin: true, email: data.user.email });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Inserisci prima l'email");
      return;
    }
    if (!isSupabaseConfigured) {
      setError("Supabase non configurato");
      return;
    }
    setLoading(true);
    setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${PROD_URL}/admin`,
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setResetSent(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setError("La password deve avere almeno 8 caratteri");
      return;
    }
    if (newPassword !== newPassword2) {
      setError("Le due password non coincidono");
      return;
    }
    if (!isSupabaseConfigured) {
      setError("Supabase non configurato");
      return;
    }
    setLoading(true);
    setError("");
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setRecoveryDone(true);
    // Lasciamo 2 secondi di feedback e poi torniamo al login pulito
    setTimeout(async () => {
      try { await supabase.auth.signOut(); } catch {}
      setNewPassword("");
      setNewPassword2("");
      setRecoveryDone(false);
      if (onRecoveryComplete) onRecoveryComplete();
    }, 2000);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 0%, rgba(192,57,43,0.18) 0%, transparent 55%),
          radial-gradient(ellipse at 80% 100%, rgba(150,40,27,0.14) 0%, transparent 55%),
          var(--bg)
        `,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", top: "-120px", left: "-120px", width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(192,57,43,0.08)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-100px", right: "-100px", width: 420, height: 420, borderRadius: "50%", border: "1px solid rgba(192,57,43,0.08)", pointerEvents: "none" }} />

      <form
        onSubmit={recoveryMode ? handleChangePassword : resetMode ? handleResetPassword : handleSubmit}
        className="animate-scale-in"
        style={{
          width: "100%",
          maxWidth: 400,
          background: "linear-gradient(145deg, rgba(192,57,43,0.08), rgba(150,40,27,0.04))",
          borderRadius: "var(--radius-2xl)",
          border: "1px solid rgba(192,57,43,0.25)",
          padding: "32px 28px",
          boxShadow: "0 20px 60px rgba(192,57,43,0.15), 0 4px 24px rgba(0,0,0,0.3)",
          backdropFilter: "blur(10px)",
        }}
      >
        {!recoveryMode && (
          <button
            type="button"
            onClick={() => {
              if (resetMode) { setResetMode(false); setResetSent(false); setError(""); }
              else onBack();
            }}
            style={{
              background: "none", border: "none", color: "var(--text-muted)",
              cursor: "pointer", fontSize: 14, padding: 0, marginBottom: 24,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <ArrowLeft size={18} /> Indietro
          </button>
        )}

        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              width: 60, height: 60, borderRadius: "var(--radius-lg)",
              margin: "0 auto 14px",
              background: "linear-gradient(135deg, #C0392B, #96281B)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 22, color: "#fff",
              boxShadow: "0 8px 24px rgba(192,57,43,0.33)",
            }}
          >
            A
          </div>
          <p style={{ color: "var(--text)", fontSize: 20, fontWeight: 700 }}>
            {recoveryMode ? "Nuova password" : resetMode ? "Recupero password" : "Amministratore"}
          </p>
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
            {recoveryMode
              ? "Imposta una nuova password per l'account admin"
              : resetMode
              ? "Riceverai un link via email per impostare una nuova password"
              : "Accedi con email e password"}
          </p>
        </div>

        {recoveryMode ? (
          recoveryDone ? (
            <div
              style={{
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.4)",
                borderRadius: 10,
                padding: "16px 18px",
                marginBottom: 14,
                textAlign: "center",
              }}
            >
              <p style={{ color: "#10b981", fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                Password aggiornata ✓
              </p>
              <p style={{ color: "var(--text-muted)", fontSize: 12 }}>
                Torno al login tra un momento...
              </p>
            </div>
          ) : (
            <>
              <label style={{ display: "block", marginBottom: 14 }}>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 6, fontWeight: 600 }}>
                  Nuova password
                </span>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="almeno 8 caratteri"
                    style={{
                      width: "100%",
                      padding: "12px 42px 12px 38px",
                      background: "rgba(0,0,0,0.25)",
                      border: "1px solid rgba(192,57,43,0.25)",
                      borderRadius: 10,
                      color: "var(--text)",
                      fontSize: 15,
                      fontFamily: "var(--font)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer",
                      padding: 6, display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>

              <label style={{ display: "block", marginBottom: 18 }}>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 6, fontWeight: 600 }}>
                  Ripeti la nuova password
                </span>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="ripeti la stessa"
                    style={{
                      width: "100%",
                      padding: "12px 14px 12px 38px",
                      background: "rgba(0,0,0,0.25)",
                      border: "1px solid rgba(192,57,43,0.25)",
                      borderRadius: 10,
                      color: "var(--text)",
                      fontSize: 15,
                      fontFamily: "var(--font)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </label>

              {error && (
                <p style={{ color: "var(--danger)", textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "13px 16px",
                  background: "linear-gradient(135deg, #C0392B, #96281B)",
                  border: "none",
                  borderRadius: 10,
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading ? "wait" : "pointer",
                  fontFamily: "var(--font)",
                  boxShadow: "0 8px 24px rgba(192,57,43,0.3)",
                  opacity: loading ? 0.7 : 1,
                  transition: "all 0.15s",
                }}
              >
                {loading ? "Attendere..." : "Salva nuova password"}
              </button>
            </>
          )
        ) : resetSent ? (
          <div
            style={{
              background: "rgba(16,185,129,0.12)",
              border: "1px solid rgba(16,185,129,0.4)",
              borderRadius: 10,
              padding: "16px 18px",
              marginBottom: 14,
              textAlign: "center",
            }}
          >
            <p style={{ color: "#10b981", fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
              Email inviata
            </p>
            <p style={{ color: "var(--text-muted)", fontSize: 12 }}>
              Controlla la casella <strong>{email}</strong> e clicca sul link per reimpostare la password.
            </p>
          </div>
        ) : (
          <>
            <label style={{ display: "block", marginBottom: 14 }}>
              <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 6, fontWeight: 600 }}>
                Email
              </span>
              <div style={{ position: "relative" }}>
                <Mail size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  placeholder="nome@azienda.it"
                  style={{
                    width: "100%",
                    padding: "12px 14px 12px 38px",
                    background: "rgba(0,0,0,0.25)",
                    border: "1px solid rgba(192,57,43,0.25)",
                    borderRadius: 10,
                    color: "var(--text)",
                    fontSize: 15,
                    fontFamily: "var(--font)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </label>

            {!resetMode && (
              <label style={{ display: "block", marginBottom: 18 }}>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 6, fontWeight: 600 }}>
                  Password
                </span>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    style={{
                      width: "100%",
                      padding: "12px 42px 12px 38px",
                      background: "rgba(0,0,0,0.25)",
                      border: "1px solid rgba(192,57,43,0.25)",
                      borderRadius: 10,
                      color: "var(--text)",
                      fontSize: 15,
                      fontFamily: "var(--font)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer",
                      padding: 6, display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
            )}

            {error && (
              <p style={{ color: "var(--danger)", textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "13px 16px",
                background: "linear-gradient(135deg, #C0392B, #96281B)",
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: loading ? "wait" : "pointer",
                fontFamily: "var(--font)",
                boxShadow: "0 8px 24px rgba(192,57,43,0.3)",
                opacity: loading ? 0.7 : 1,
                transition: "all 0.15s",
              }}
            >
              {loading ? "Attendere..." : resetMode ? "Invia link di recupero" : "Accedi"}
            </button>

            {!resetMode && (
              <button
                type="button"
                onClick={() => { setResetMode(true); setError(""); }}
                style={{
                  width: "100%",
                  marginTop: 14,
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: "var(--font)",
                  textDecoration: "underline",
                }}
              >
                Password dimenticata?
              </button>
            )}
          </>
        )}
      </form>
    </div>
  );
}
